import { useId } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * Un campo data che si legge allo stesso modo su qualunque computer.
 *
 * `<input type="date">` mostra la data nel formato della **lingua
 * dell'interfaccia del browser**, non in quello della pagina: `lang="it"` non
 * c'entra, e nemmeno `navigator.language` o `Intl`. Provato: stesso documento,
 * `17/10/2026` con un Chromium avviato `--lang=it-IT` e `10/17/2026` con la
 * lingua di sistema. Provato anche che `lang` sull'input non serve a niente —
 * `lang="it-IT"` e `lang="en-US"` rendono identici.
 *
 * Qui dentro passano le scadenze dei preventivi. `03/04/2026` letto
 * all'americana è un mese di differenza, e nessuno se ne accorge finché non è
 * tardi. Quindi: si tiene il campo nativo — che porta il calendario e, sul
 * telefono, la tastiera giusta — e sotto ci si scrive la data **per esteso**,
 * che è inequivocabile in qualunque lingua sia il browser.
 *
 * Se non c'è una data, sotto non c'è niente: un campo vuoto non si commenta.
 */

const MESI = [
  "gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
  "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre",
];

export function dataPerEsteso(iso) {
  if (typeof iso !== "string") return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!m) return null;
  const [, a, me, g] = m.map(Number);
  if (me < 1 || me > 12 || g < 1 || g > 31) return null;
  /* Costruita con i pezzi, non con `new Date(iso)`: quello legge la stringa
     come mezzanotte UTC e a ovest di Greenwich restituisce il giorno prima. */
  const d = new Date(a, me - 1, g);
  if (d.getFullYear() !== a || d.getMonth() !== me - 1 || d.getDate() !== g) return null; // 31 di febbraio
  return `${g} ${MESI[me - 1]} ${a}`;
}

export default function CampoData({ className, testId, id, ...props }) {
  const auto = useId();
  const idCampo = id || auto;
  const idLettura = `${idCampo}-per-esteso`;
  const esteso = dataPerEsteso(props.value ?? props.defaultValue);

  return (
    <div className={cn("grid gap-micro", className)}>
      <Input
        id={idCampo}
        type="date"
        data-testid={testId}
        aria-describedby={esteso ? idLettura : undefined}
        {...props}
      />
      {esteso ? (
        <p id={idLettura} data-testid={testId ? `${testId}-per-esteso` : undefined} className="text-xs text-muted-foreground">
          {esteso}
        </p>
      ) : null}
    </div>
  );
}
